'use client'

import {
  ClerkProvider,
  RedirectToSignIn,
  useAuth,
  useUser,
} from "@clerk/nextjs";
import "./globals.css";

import AppLayout from "@/components/app-layout";
import { PreferencesProvider } from "@/contexts/preferences-context";
import { ToastProvider } from "@/context/ToastProvider";
import { usePathname } from "next/navigation";
import OrgGuard from "@/components/org-guard";
import OneSignalProvider from "@/components/onesignal-provider";
import { GlobalMemoryProvider } from "@/contexts/global-memory-context";
import { identifyPostHogUser } from "@/lib/posthog-client";
import { useEffect, useMemo } from "react";

function LayoutContent({ children }) {
  const pathname = usePathname();
  const isPopout = pathname?.includes('/popout');
  const isOverlay = pathname?.startsWith('/overlay');
  const isRoot = pathname === '/';
  const isSsoCallback = pathname?.startsWith('/sso-callback');

  if (isPopout || isOverlay || isRoot || isSsoCallback) {
    return children;
  }

  return (
    <AppLayout>
      {children}
    </AppLayout>
  );
}

function AuthenticatedLayout({ children }) {
  const { isLoaded, isSignedIn, orgId } = useAuth();
  const { isLoaded: isUserLoaded, user } = useUser();
  const subscriptionData = useMemo(() => user ? ({
    id: user.id,
    fullName: user.fullName,
    firstName: user.firstName,
    imageUrl: user.imageUrl,
    organizationId: orgId,
  }) : null, [orgId, user]);

  useEffect(() => {
    if (!isLoaded || !isUserLoaded || !isSignedIn || !user) return;

    identifyPostHogUser(user.id, {
      email: user.primaryEmailAddress?.emailAddress,
      name: user.fullName,
      organization_id: orgId,
    });
  }, [isLoaded, isSignedIn, isUserLoaded, orgId, user]);

  if (!isLoaded || !isUserLoaded) {
    return null;
  }

  if (!isSignedIn) {
    return <RedirectToSignIn />;
  }

  return (
    <OneSignalProvider>
      <ToastProvider>
        <PreferencesProvider>
          <GlobalMemoryProvider subscriptionData={subscriptionData}>
            <OrgGuard>
              <LayoutContent>
                {children}
              </LayoutContent>
            </OrgGuard>
          </GlobalMemoryProvider>
        </PreferencesProvider>
      </ToastProvider>
    </OneSignalProvider>
  );
}

export default function RootLayout({ children }) {
  const pathname = usePathname();
  const isOverlay = pathname?.startsWith('/overlay');
  const isRoot = pathname === '/';
  const isSsoCallback = pathname?.startsWith('/sso-callback');

  return (
    <ClerkProvider>
      <html lang="en">
        <head>
          <title>WXLOGIC Portal</title>
          <meta name="application-name" content="WXLOGIC Portal" />
          <link
            rel="icon"
            type="image/png"
            href="/wxlogic_white_square_icon_1024_transparent.png"
          />
          <link
            rel="apple-touch-icon"
            href="/wxlogic_white_square_icon_1024_transparent.png"
          />
        </head>
        <body
          className="antialiased"
        >
          {isOverlay ? (
            children
          ) : isRoot || isSsoCallback ? (
            <LayoutContent>
              {children}
            </LayoutContent>
          ) : (
            <AuthenticatedLayout>{children}</AuthenticatedLayout>
          )}
        </body>
      </html>
    </ClerkProvider>
  );
}

